import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { MDText } from 'i18n-react';
import Button from '@adsk/bim360-matrix-react-button';
import PermissionIndicator from '@adsk/bim360-matrix-react-permission-indicator';
import SelectBox from '@adsk/bim360-matrix-react-select-box';
import { withLocale, TSymbolProp } from '@adsk/bim360-matrix-react-helpers/es/locale';

import './_PermissionSelector.scss';

export class PermissionSelector extends React.Component {
  constructor(props) {
    super(props);

    const selectedPermission = this.getSelectedPermission(props.selectedPermissionId);

    this.state = {
      selectedPermission,
      dropdownOpen: false,
      learnMoreClicked: false
    };

    this._handleOnSelect = this._handleOnSelect.bind(this);
    this.renderLearnMore = this.renderLearnMore.bind(this);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (
      nextProps.selectedPermissionId &&
      this.state.selectedPermission &&
      nextProps.selectedPermissionId !== this.state.selectedPermission.id
    ) {
      const selectedPermission = this.getSelectedPermission(nextProps.selectedPermissionId);

      this.setState({
        selectedPermission
      });
    }
  }

  onDropdownOpen = () => {
    this.setState({ dropdownOpen: true });
    this.props.onOpen && this.props.onOpen();
  };

  onDropdownClose = () => {
    this.setState({ dropdownOpen: false });
    this.props.onClose && this.props.onClose();
  };

  getSelectedPermission(id) {
    return (
      this.props.permissions.find(permission => permission.id === id) || {
        label: '',
        value: [],
        id: 'NOT_SPECIFIED'
      }
    );
  }

  _handleOnSelect({ eventKey: permissionKey }) {
    let selectedPermissionId;
    if (permissionKey) {
      selectedPermissionId = permissionKey.split('|')[0];
    }

    this.setState({
      selectedPermission: selectedPermissionId
        ? this.getSelectedPermission(selectedPermissionId)
        : null,
      dropdownOpen: false
    });

    if (this.props.onClick) {
      this.props.onClick(selectedPermissionId);
    }
  }

  createEventKey(permission) {
    return `${permission.id}|${permission.label}`;
  }

  renderLearnMore() {
    return (
      <div className="PermissionSelector__learn-more-container">
        <Button
          styleType={Button.StyleType.LINK_SECONDARY}
          className="PermissionSelector__learn-more"
          onClick={this.props.onLearnMore}
        >
          {this.props[TSymbolProp].translate('LEARN_MORE')}
        </Button>
      </div>
    );
  }

  renderSelector() {
    const permissionIndicatorProps = {
      label: this.state.selectedPermission.label,
      value: this.state.selectedPermission.value,
      pillRenderer: this.state.selectedPermission.pillRenderer,
      pillCount:
        this.props.indicatorPillCount ||
        Math.max(...this.props.permissions.map(p => Math.max(...p.value))) + 1 // +1 Due to zero-based index
    };
    const selectedPermission = <PermissionIndicator {...permissionIndicatorProps} />;
    const options = this.props.permissions.map(permission => ({
      ...permission,
      eventKey: this.createEventKey(permission)
    }));

    const isOptionDisabled = option => option.disabled;

    const refHandler = node => {
      this.dropdownRef = node;
      if (this.props.menuRef) {
        this.props.menuRef(node);
      }
    };

    return this.props.readOnly ? (
      selectedPermission
    ) : (
      <SelectBox
        ref={refHandler}
        className="PermissionSelector__select-box"
        menuClassName="PermissionSelector__select-box-menu"
        container={this.props.container}
        options={options}
        onChange={this._handleOnSelect}
        onOpen={this.onDropdownOpen}
        onClose={this.onDropdownClose}
        footerRenderer={this.props.onLearnMore ? this.renderLearnMore : undefined}
        placeholder={selectedPermission}
        valueRenderer={() => selectedPermission}
        value={this.state.selectedPermission}
        isOptionDisabled={isOptionDisabled}
      />
    );
  }

  render() {
    const selectorClasses = classNames('PermissionSelector', {
      'PermissionSelector--open': this.state.dropdownOpen,
      'PermissionSelector--readonly': this.props.readOnly
    });

    return (
      <div className={selectorClasses}>
        <div className="PermissionSelector__indicator-wrapper">{this.renderSelector()}</div>
      </div>
    );
  }
}

PermissionSelector.defaultProps = {
  indicatorPillCount: 0
};

PermissionSelector.propTypes = {
  /**
   * A function that is called when clicking on the "learn more" button. If not set the "learn more" button isn't visible.
   */
  onLearnMore: PropTypes.func,
  /**
   * Array of permissions
   */
  permissions: PropTypes.arrayOf(
    PropTypes.shape({
      /**
       * Label of the permission to be displayed in the dropdown and in the Permission Indicator
       */
      label: PropTypes.string.isRequired,
      /**
       * Array of indices (zero-based) which indicates which pills will be "selected" (colored).
       * For example, if I have 3 pills and a permission that requires the 2nd and 3rd pill to be colored, then [1, 2] should be passed to this prop.
       */
      value: PropTypes.array.isRequired,
      /**
       * Id of the selected permission, will be reported back upon onClick
       */
      id: PropTypes.string.isRequired,
      /**
       * A callback function to custom render a pill with the shape of `({index, value}) => node`
       */
      pillRenderer: PropTypes.func,
      /**
       * Whether the permission option should be disabled
       */
      disabled: PropTypes.bool
    })
  ).isRequired,
  /**
   * Id of the selected permission
   */
  selectedPermissionId: PropTypes.string,
  /**
   * onClick handler to be executed when a permission is selected
   */
  onClick: PropTypes.func,
  /**
   * onOpen handler to be executed when the options menu is opened
   */
  onOpen: PropTypes.func,
  /**
   * onClose handler to be executed when the options menu is closed
   */
  onClose: PropTypes.func,
  /**
   * If true, the indicator will be shown but no onClick & dropdown menu will be available
   */
  readOnly: PropTypes.bool,
  /**
   * Locale of the component
   */
  locale: PropTypes.string,
  /**
   * Number of pills to show
   */
  indicatorPillCount: PropTypes.number,
  /**
   * SelectBox container
   */
  container: PropTypes.object,
  /**
   * A function to get the ref to the menu element
   */
  menuRef: PropTypes.func
};

export default withLocale(
  PermissionSelector,
  ({ locale }) => new MDText(require(`./i18n/${locale}.json`))
);
